import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { isLocalEnvironment } from "$routes/_lib/environment";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "epocus.onboarding.agentUrl";
const PARAM = "agent";
const LOOPBACK = ["localhost", "127.0.0.1", "[::1]"];

// The surface sends the signed-in admin's token to this origin, so a crafted
// `?agent=` link would otherwise exfiltrate it.
const isLoopback = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      ["http:", "https:"].includes(url.protocol) &&
      LOOPBACK.includes(url.hostname)
    );
  } catch {
    return false;
  }
};

// Storage blocked or disabled behaves as no override rather than crashing the
// render, as `use-default-device.ts` does.
const remembered = (): string | undefined => {
  try {
    return localStorage.getItem(STORAGE_KEY)?.trim();
  } catch {
    return undefined;
  }
};

const remember = (value: string | undefined): void => {
  try {
    if (value === undefined) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Not persisted; the override still holds for this navigation.
  }
};

export const devAgentUrl = (search: string): string | undefined => {
  if (!isLocalEnvironment()) return undefined;

  const requested = new URLSearchParams(search).get(PARAM)?.trim();
  if (requested !== undefined) {
    if (!isLoopback(requested)) {
      remember(undefined);
      return undefined;
    }
    remember(requested);
    return requested;
  }

  const stored = remembered();
  return stored && isLoopback(stored) ? stored : undefined;
};

export const useAgentUrl = (): string | undefined => {
  const [flagValue] = useFeatureFlag<string>(
    featureFlags.epocus.onboardingAgentUrl,
  );
  return devAgentUrl(useLocation().search) ?? flagValue?.trim();
};
